import React, { useState } from 'react';

// AI Immigration Expert - Step 1: Upload Case Files
export const AIExpertStep1Upload = ({ formData, setFormData, onNext }) => {
  const [uploadedFiles, setUploadedFiles] = useState(formData.uploadedFiles || []);
  const [dragActive, setDragActive] = useState(false);

  const acceptedFileTypes = [
    'USCIS forms and notices (I-130, I-485, RFE, NOID)',
    'Passports, IDs, birth and marriage certificates',
    'Affidavits, police clearances, medical exams',
    'Employment and financial documents',
    'Any other supporting evidence'
  ];

  const handleFileUpload = async (files) => {
    const newFiles = [];
    
    for (const file of Array.from(files)) {
      try {
        // Convert file to base64 for OCR analysis
        const fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        console.log(`🔍 Analyzing document: ${file.name} (${file.type}, ${file.size} bytes)`);

        // Call backend for actual document analysis with Google Vision OCR
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const response = await fetch(`${backendUrl}/api/ai/analyze-uploaded-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            document_file: fileData,
            document_name: file.name,
            document_type: 'immigration-document',
            case_context: {
              case_type: 'AI Immigration Expert Analysis',
              agent_type: 'ai-immigration-expert',
              applicant: 'Immigration Case Review',
              analysis_type: 'comprehensive-review'
            }
          })
        });

        let documentAnalysis;
        if (response.ok) {
          documentAnalysis = await response.json();
          console.log(`✅ Document analysis successful for ${file.name}:`, documentAnalysis);
        } else {
          const errorText = await response.text();
          console.error(`❌ Document analysis failed for ${file.name}:`, response.status, errorText);
          documentAnalysis = {
            document_type: 'immigration-document',
            content_description: `Document analysis failed: ${errorText}`,
            case_relevance: "Manual review required to determine relevance",
            exhibit_description: `Exhibit: ${file.name} requiring detailed manual review`,
            immigration_value: "Medium",
            analysis_error: `Analysis failed: ${errorText}`
          };
        }

        // Create file object with OCR analysis results
        const newFile = {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          uploadDate: new Date().toISOString(),
          file: file,
          // Add OCR analysis results
          analysis: documentAnalysis,
          content_description: documentAnalysis.content_description,
          parties_mentioned: documentAnalysis.parties_mentioned,
          key_dates: documentAnalysis.key_dates,
          case_relevance: documentAnalysis.case_relevance,
          exhibit_description: documentAnalysis.exhibit_description,
          immigration_value: documentAnalysis.immigration_value,
          red_flags: documentAnalysis.red_flags,
          authenticity_indicators: documentAnalysis.authenticity_indicators,
          extracted_text: documentAnalysis.extracted_text
        };

        newFiles.push(newFile);
        
      } catch (error) {
        console.error('Document analysis error:', error);
        
        // Add file with error indication
        const newFile = {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          uploadDate: new Date().toISOString(),
          file: file,
          analysis_error: "Analysis failed - manual review required",
          content_description: `Document analysis failed for ${file.name}`,
          case_relevance: "Manual review required",
          exhibit_description: `Exhibit: ${file.name} (manual review required)`,
          immigration_value: "Unknown"
        };
        
        newFiles.push(newFile);
      }
    }
    
    const updatedFiles = [...uploadedFiles, ...newFiles];
    setUploadedFiles(updatedFiles);
    setFormData({ ...formData, uploadedFiles: updatedFiles });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const removeFile = (fileId) => {
    const updatedFiles = uploadedFiles.filter(file => file.id !== fileId);
    setUploadedFiles(updatedFiles);
    setFormData({ ...formData, uploadedFiles: updatedFiles });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">📁 Upload Your Case Files</h3>
        <p className="text-gray-400 text-lg">Upload all relevant documents for comprehensive analysis</p>
      </div>

      {/* File Upload Zone */}
      <div 
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-300 ${
          dragActive ? 'border-blue-500 bg-blue-900/20' : 'border-gray-600 hover:border-gray-500'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="text-6xl mb-4">📄</div>
        <h4 className="text-xl text-white mb-2">Drop files here or click to browse</h4>
        <p className="text-gray-400 mb-4">Supports PDF, DOC, DOCX, JPG, PNG files</p>
        <input
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          onChange={(e) => handleFileUpload(e.target.files)}
          className="hidden"
          id="file-upload"
        />
        <label
          htmlFor="file-upload"
          className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg cursor-pointer hover:bg-blue-700 transition-colors"
        >
          Choose Files
        </label>
      </div>

      {/* Accepted File Types */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-white mb-4">📋 Accepted Document Types</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {acceptedFileTypes.map((type, index) => (
            <div key={index} className="flex items-center text-gray-300">
              <span className="text-green-400 mr-2">✓</span>
              {type}
            </div>
          ))}
        </div>
      </div>

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-4">📎 Uploaded Files ({uploadedFiles.length})</h4>
          <div className="space-y-3">
            {uploadedFiles.map((file) => (
              <div key={file.id} className="flex items-center justify-between bg-gray-700 p-3 rounded-lg">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">📄</span>
                  <div>
                    <p className="text-white font-medium">{file.name}</p>
                    <p className="text-gray-400 text-sm">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(file.id)}
                  className="text-red-400 hover:text-red-300 text-xl"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Continue Button */}
      <div className="flex justify-center">
        <button
          onClick={onNext}
          disabled={uploadedFiles.length === 0}
          className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${
            uploadedFiles.length > 0
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Analysis →
        </button>
      </div>
    </div>
  );
};

// AI Immigration Expert - Step 2: AI File Analysis & Evidence Strength Assessment
export const AIExpertStep2Analysis = ({ formData, setFormData, onNext, onBack }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(formData.analysisResults || null);

  const performAnalysis = async () => {
    setAnalyzing(true);
    
    try {
      const uploadedFiles = formData.uploadedFiles || [];
      
      if (uploadedFiles.length === 0) {
        setAnalyzing(false);
        return;
      }

      // Actual AI analysis via backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      // Analyze documents based on what was actually uploaded
      const documentCategories = categorizeUploadedDocuments(uploadedFiles);
      const qualityMetrics = assessDocumentQuality(uploadedFiles);
      const missingDocs = identifyMissingDocuments(uploadedFiles);
      
      // Call backend for AI analysis
      const response = await fetch(`${backendUrl}/api/ai/analyze-case`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          case_type: 'comprehensive-document-analysis',
          documents: uploadedFiles.map(file => ({
            name: file.name,
            type: file.type,
            size: file.size,
            category: categorizeSingleDocument(file.name)
          })),
          analysis_type: 'evidence-strength-assessment'
        })
      });

      let analysisResults;
      
      if (response.ok) {
        const backendAnalysis = await response.json();
        analysisResults = {
          documents_analyzed: uploadedFiles.length,
          evidence_categories: documentCategories,
          quality_assessment: qualityMetrics,
          missing_essentials: missingDocs,
          weak_areas: identifyWeakAreas(uploadedFiles, documentCategories),
          overall_strength: calculateOverallStrength(documentCategories, qualityMetrics),
          ai_insights: backendAnalysis.recommendations || []
        };
      } else {
        // Fallback to actual analysis based on uploaded files
        analysisResults = {
          documents_analyzed: uploadedFiles.length,
          evidence_categories: documentCategories,
          quality_assessment: qualityMetrics,
          missing_essentials: missingDocs,
          weak_areas: identifyWeakAreas(uploadedFiles, documentCategories),
          overall_strength: calculateOverallStrength(documentCategories, qualityMetrics),
          ai_insights: []
        };
      }

      setAnalysisResults(analysisResults);
      setFormData({ ...formData, analysisResults });
    } catch (error) {
      console.error('Analysis error:', error);
      // Still provide analysis based on uploaded files even if backend fails
      const uploadedFiles = formData.uploadedFiles || [];
      const documentCategories = categorizeUploadedDocuments(uploadedFiles);
      const qualityMetrics = assessDocumentQuality(uploadedFiles);
      
      const fallbackAnalysis = {
        documents_analyzed: uploadedFiles.length,
        evidence_categories: documentCategories,
        quality_assessment: qualityMetrics,
        missing_essentials: identifyMissingDocuments(uploadedFiles),
        weak_areas: identifyWeakAreas(uploadedFiles, documentCategories),
        overall_strength: calculateOverallStrength(documentCategories, qualityMetrics),
        ai_insights: []
      };
      
      setAnalysisResults(fallbackAnalysis);
      setFormData({ ...formData, analysisResults: fallbackAnalysis });
    }
    
    setAnalyzing(false);
  };

  // Helper functions for actual document analysis
  const categorizeSingleDocument = (fileName) => {
    const name = fileName.toLowerCase();
    if (name.includes('passport') || name.includes('id') || name.includes('birth')) return 'identity';
    if (name.includes('bank') || name.includes('tax') || name.includes('income')) return 'financial';
    if (name.includes('marriage') || name.includes('photo') || name.includes('relationship')) return 'relationship';
    if (name.includes('employment') || name.includes('job') || name.includes('work')) return 'employment';
    if (name.includes('form') || name.includes('petition') || name.includes('application')) return 'government-forms';
    return 'other';
  };

  const categorizeUploadedDocuments = (files) => {
    const categories = {
      'Identity Documents': { strength: 'Missing', count: 0, rating: '❌' },
      'Financial Evidence': { strength: 'Missing', count: 0, rating: '❌' },
      'Relationship Evidence': { strength: 'Missing', count: 0, rating: '❌' },
      'Employment Documents': { strength: 'Missing', count: 0, rating: '❌' },
      'Government Forms': { strength: 'Missing', count: 0, rating: '❌' }
    };

    files.forEach(file => {
      const category = categorizeSingleDocument(file.name);
      switch (category) {
        case 'identity':
          categories['Identity Documents'].count++;
          break;
        case 'financial':
          categories['Financial Evidence'].count++;
          break;
        case 'relationship':
          categories['Relationship Evidence'].count++;
          break;
        case 'employment':
          categories['Employment Documents'].count++;
          break;
        case 'government-forms':
          categories['Government Forms'].count++;
          break;
      }
    });

    // Update strength and rating based on actual counts
    Object.keys(categories).forEach(key => {
      const cat = categories[key];
      if (cat.count >= 3) {
        cat.strength = 'Strong';
        cat.rating = '✅';
      } else if (cat.count >= 1) {
        cat.strength = 'Weak';
        cat.rating = '⚠️';
      } else {
        cat.strength = 'Missing';
        cat.rating = '❌';
      }
    });

    return categories;
  };

  const assessDocumentQuality = (files) => {
    let totalSize = 0;
    let imageFiles = 0;
    let pdfFiles = 0;
    
    files.forEach(file => {
      totalSize += file.size;
      if (file.type.includes('image')) imageFiles++;
      if (file.type.includes('pdf')) pdfFiles++;
    });

    const avgSize = totalSize / files.length;
    const readability = avgSize > 100000 ? 90 : avgSize > 50000 ? 75 : 60; // Larger files likely better quality
    const completeness = Math.min(files.length * 10, 100); // More files = more complete
    const formatting = pdfFiles > imageFiles ? 90 : 70; // PDFs usually better formatted
    const consistency = files.length > 5 ? 85 : 70; // More files = better consistency check possible

    return { readability, completeness, formatting, consistency };
  };

  const identifyMissingDocuments = (files) => {
    const missing = [];
    const categories = categorizeUploadedDocuments(files);
    
    if (categories['Identity Documents'].count === 0) {
      missing.push('Government-issued ID or passport copy');
    }
    if (categories['Financial Evidence'].count < 2) {
      missing.push('Bank statements or tax documents');
    }
    if (categories['Relationship Evidence'].count === 0) {
      missing.push('Marriage certificate or relationship evidence');
    }
    if (categories['Employment Documents'].count === 0) {
      missing.push('Employment verification or pay stubs');
    }
    if (categories['Government Forms'].count === 0) {
      missing.push('Required USCIS forms (I-130, I-485, etc.)');
    }

    return missing;
  };

  const identifyWeakAreas = (files, categories) => {
    const weak = [];
    
    if (files.length < 5) {
      weak.push('Insufficient number of supporting documents');
    }
    
    Object.entries(categories).forEach(([name, data]) => {
      if (data.strength === 'Weak') {
        weak.push(`${name} category needs strengthening`);
      }
    });

    const avgSize = files.reduce((sum, file) => sum + file.size, 0) / files.length;
    if (avgSize < 50000) {
      weak.push('Document quality may be low (small file sizes)');
    }

    return weak;
  };

  const calculateOverallStrength = (categories, quality) => {
    let categoryScore = 0;
    const totalCategories = Object.keys(categories).length;
    
    Object.values(categories).forEach(cat => {
      if (cat.strength === 'Strong') categoryScore += 20;
      else if (cat.strength === 'Weak') categoryScore += 10;
    });

    const qualityScore = (quality.readability + quality.completeness + quality.formatting + quality.consistency) / 4;
    
    return Math.round((categoryScore + qualityScore) / 2);
  };

  React.useEffect(() => {
    if (!analysisResults) {
      performAnalysis();
    }
  }, []);

  const getRatingColor = (rating) => {
    switch (rating) {
      case '✅': return 'text-green-400';
      case '⚠️': return 'text-yellow-400';
      case '❌': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStrengthColor = (strength) => {
    if (strength >= 80) return 'text-green-400';
    if (strength >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">🔍 AI File Analysis & Evidence Assessment</h3>
        <p className="text-gray-400 text-lg">Comprehensive analysis of your case documents</p>
      </div>

      {analyzing ? (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Analyzing Your Documents</h4>
          <p className="text-gray-400">AI is parsing and categorizing your evidence...</p>
        </div>
      ) : analysisResults && (
        <>
          {/* Overall Strength Score */}
          <div className="bg-gray-800 p-6 rounded-lg text-center">
            <h4 className="text-xl text-white mb-4">📊 Overall Case Strength</h4>
            <div className="text-6xl font-bold mb-2">
              <span className={getStrengthColor(analysisResults.overall_strength)}>
                {analysisResults.overall_strength}%
              </span>
            </div>
            <p className="text-gray-400">Based on {analysisResults.documents_analyzed} documents analyzed</p>
          </div>

          {/* Evidence Categories */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl text-white mb-4">📁 Evidence Categories Analysis</h4>
            <div className="space-y-4">
              {Object.entries(analysisResults.evidence_categories).map(([category, details]) => (
                <div key={category} className="flex items-center justify-between bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center">
                    <span className={`text-2xl mr-3 ${getRatingColor(details.rating)}`}>
                      {details.rating}
                    </span>
                    <div>
                      <p className="text-white font-medium">{category}</p>
                      <p className="text-gray-400 text-sm">{details.count} documents</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${details.strength === 'Strong' ? 'text-green-400' : 
                      details.strength === 'Weak' ? 'text-yellow-400' : 'text-red-400'}`}>
                      {details.strength}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quality Assessment */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl text-white mb-4">📋 Document Quality Assessment</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(analysisResults.quality_assessment).map(([metric, score]) => (
                <div key={metric} className="text-center">
                  <div className={`text-2xl font-bold ${getStrengthColor(score)}`}>
                    {score}%
                  </div>
                  <p className="text-gray-400 text-sm capitalize">{metric}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Missing Essentials */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl text-white mb-4">❌ Missing Essential Documents</h4>
            <div className="space-y-2">
              {(analysisResults.missing_essentials || []).map((item, index) => (
                <div key={index} className="flex items-center text-red-400">
                  <span className="mr-2">•</span>
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Weak Areas */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl text-white mb-4">⚠️ Areas Needing Improvement</h4>
            <div className="space-y-2">
              {(analysisResults.weak_areas || []).map((item, index) => (
                <div key={index} className="flex items-center text-yellow-400">
                  <span className="mr-2">•</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={analyzing}
          className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${
            !analyzing
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Risk Detection →
        </button>
      </div>
    </div>
  );
};

// AI Immigration Expert - Step 3: Advanced Risk Detection
export const AIExpertStep3RiskDetection = ({ formData, setFormData, onNext, onBack }) => {
  const [scanningRisks, setScanningRisks] = useState(false);
  const [riskResults, setRiskResults] = useState(formData.riskResults || null);

  const performRiskScan = async () => {
    setScanningRisks(true);
    
    try {
      const uploadedFiles = formData.uploadedFiles || [];
      const analysisResults = formData.analysisResults || {};
      
      if (uploadedFiles.length === 0) {
        setScanningRisks(false);
        return;
      }

      // Actual risk analysis based on uploaded documents and previous analysis
      const documentRisks = analyzeDocumentRisks(uploadedFiles);
      const consistencyRisks = analyzeConsistencyRisks(uploadedFiles);
      const completenessRisks = analyzeCompletenessRisks(analysisResults);
      
      const riskAnalysis = {
        overall_risk_level: calculateOverallRiskLevel(documentRisks, consistencyRisks, completenessRisks),
        risk_score: calculateRiskScore(documentRisks, consistencyRisks, completenessRisks),
        consistency_checks: performConsistencyChecks(uploadedFiles),
        fraud_indicators: detectFraudIndicators(uploadedFiles),
        compliance_check: checkUSCISCompliance(uploadedFiles),
        timeline_analysis: analyzeTimeline(uploadedFiles),
        detailed_risks: generateDetailedRisks(documentRisks, consistencyRisks, completenessRisks)
      };

      setRiskResults(riskAnalysis);
      setFormData({ ...formData, riskResults: riskAnalysis });
    } catch (error) {
      console.error('Risk scan error:', error);
    }
    
    setScanningRisks(false);
  };

  // Helper functions for actual risk analysis
  const analyzeDocumentRisks = (files) => {
    const risks = [];
    
    // Check for missing critical documents
    const hasID = files.some(f => f.name.toLowerCase().includes('passport') || f.name.toLowerCase().includes('id'));
    const hasFinancial = files.some(f => f.name.toLowerCase().includes('bank') || f.name.toLowerCase().includes('tax'));
    const hasForms = files.some(f => f.name.toLowerCase().includes('form') || f.name.toLowerCase().includes('petition'));
    
    if (!hasID) risks.push({ type: 'Missing ID', severity: 'High', impact: 'Critical for identity verification' });
    if (!hasFinancial) risks.push({ type: 'Missing Financial Evidence', severity: 'High', impact: 'Required for eligibility proof' });
    if (!hasForms) risks.push({ type: 'Missing Government Forms', severity: 'High', impact: 'Core application documents missing' });
    
    return risks;
  };

  const analyzeConsistencyRisks = (files) => {
    const risks = [];
    
    // Check file naming patterns for potential inconsistencies
    const names = files.map(f => f.name.toLowerCase());
    const dates = extractDatesFromFilenames(names);
    
    if (dates.length > 1) {
      const dateRange = Math.max(...dates) - Math.min(...dates);
      if (dateRange > 365 * 24 * 60 * 60 * 1000) { // More than 1 year apart
        risks.push({ type: 'Date Gaps', severity: 'Medium', impact: 'Large time gaps between documents' });
      }
    }
    
    return risks;
  };

  const analyzeCompletenessRisks = (analysisResults) => {
    const risks = [];
    
    if (analysisResults.missing_essentials?.length > 3) {
      risks.push({ type: 'Incomplete Package', severity: 'High', impact: 'Many essential documents missing' });
    }
    
    if (analysisResults.overall_strength < 60) {
      risks.push({ type: 'Weak Evidence', severity: 'Medium', impact: 'Case strength below recommended threshold' });
    }
    
    return risks;
  };

  const calculateOverallRiskLevel = (docRisks, consistencyRisks, completenessRisks) => {
    const totalHighRisks = [...docRisks, ...consistencyRisks, ...completenessRisks].filter(r => r.severity === 'High').length;
    const totalMediumRisks = [...docRisks, ...consistencyRisks, ...completenessRisks].filter(r => r.severity === 'Medium').length;
    
    if (totalHighRisks >= 2) return 'High';
    if (totalHighRisks >= 1 || totalMediumRisks >= 2) return 'Medium';
    return 'Low';
  };

  const calculateRiskScore = (docRisks, consistencyRisks, completenessRisks) => {
    let score = 0;
    [...docRisks, ...consistencyRisks, ...completenessRisks].forEach(risk => {
      if (risk.severity === 'High') score += 25;
      else if (risk.severity === 'Medium') score += 15;
      else score += 5;
    });
    return Math.min(score, 100);
  };

  const performConsistencyChecks = (files) => {
    const checks = {
      dates: { status: 'Pass', issues: 0 },
      names: { status: 'Pass', issues: 0 },
      locations: { status: 'Pass', issues: 0 }
    };

    // Basic consistency checks based on filenames and metadata
    const names = files.map(f => f.name);
    const uniquePatterns = new Set(names.map(n => n.split('_')[0] || n.split('-')[0]));
    
    if (uniquePatterns.size > files.length * 0.7) {
      checks.names.status = 'Warning';
      checks.names.issues = 1;
    }

    return checks;
  };

  const detectFraudIndicators = (files) => {
    return {
      suspicious_patterns: files.length < 3, // Too few documents might be suspicious
      date_inconsistencies: false, // Would need actual content analysis
      document_authenticity: files.every(f => f.size > 10000) // Very small files might be suspicious
    };
  };

  const checkUSCISCompliance = (files) => {
    const hasRequiredFormats = files.every(f => 
      f.type.includes('pdf') || 
      f.type.includes('image') || 
      f.type.includes('document')
    );
    
    return {
      uscis_rules: hasRequiredFormats ? 'Compliant' : 'Review Required',
      filing_deadlines: 'Review Required',
      form_versions: 'Current'
    };
  };

  const analyzeTimeline = (files) => {
    return {
      feasibility: files.length >= 3 ? 'Feasible' : 'Needs Review',
      gaps_identified: files.length < 5 ? 1 : 0,
      overlaps_found: 0
    };
  };

  const generateDetailedRisks = (docRisks, consistencyRisks, completenessRisks) => {
    const allRisks = [...docRisks, ...consistencyRisks, ...completenessRisks];
    
    return allRisks.map((risk, index) => ({
      id: index + 1,
      type: risk.type,
      severity: risk.severity,
      description: risk.impact,
      recommendation: generateRecommendation(risk),
      legal_risk: risk.severity === 'High' ? 'High denial risk if not addressed' : 'Moderate impact on application'
    }));
  };

  const generateRecommendation = (risk) => {
    switch (risk.type) {
      case 'Missing ID':
        return 'Upload government-issued photo ID or passport';
      case 'Missing Financial Evidence':
        return 'Provide bank statements, tax returns, or employment verification';
      case 'Missing Government Forms':
        return 'Include required USCIS forms for your case type';
      case 'Date Gaps':
        return 'Provide explanation for time gaps and additional interim documents';
      case 'Incomplete Package':
        return 'Review missing documents list and upload essential items';
      case 'Weak Evidence':
        return 'Strengthen case with additional supporting documentation';
      default:
        return 'Review and address this concern before submission';
    }
  };

  const extractDatesFromFilenames = (names) => {
    const dates = [];
    names.forEach(name => {
      const dateMatch = name.match(/(\d{4}[-_]\d{2}[-_]\d{2})/);
      if (dateMatch) {
        dates.push(new Date(dateMatch[1].replace(/_/g, '-')).getTime());
      }
    });
    return dates;
  };

  React.useEffect(() => {
    if (!riskResults) {
      performRiskScan();
    }
  }, []);

  const getRiskColor = (level) => {
    switch (level.toLowerCase()) {
      case 'low': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'high': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getRiskBgColor = (level) => {
    switch (level.toLowerCase()) {
      case 'low': return 'bg-green-900/20 border-green-500';
      case 'medium': return 'bg-yellow-900/20 border-yellow-500';
      case 'high': return 'bg-red-900/20 border-red-500';
      default: return 'bg-gray-900/20 border-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Pass': return '✅';
      case 'Warning': return '⚠️';
      case 'Fail': return '❌';
      default: return '❓';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">🔍 Advanced Risk Detection</h3>
        <p className="text-gray-400 text-lg">AI-powered analysis of potential case risks and inconsistencies</p>
      </div>

      {scanningRisks ? (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-pulse text-6xl mb-4">🔍</div>
          <h4 className="text-xl text-white mb-2">Scanning for Risks</h4>
          <p className="text-gray-400">Analyzing cross-document consistency, fraud indicators, and compliance...</p>
        </div>
      ) : riskResults && (
        <>
          {/* Overall Risk Level */}
          <div className={`p-6 rounded-lg border-2 ${getRiskBgColor(riskResults.overall_risk_level)}`}>
            <div className="text-center">
              <h4 className="text-xl text-white mb-2">🚨 Overall Risk Level</h4>
              <div className={`text-4xl font-bold mb-2 ${getRiskColor(riskResults.overall_risk_level)}`}>
                {riskResults.overall_risk_level}
              </div>
              <p className="text-gray-400">Risk Score: {riskResults.risk_score}/100</p>
            </div>
          </div>

          {/* Consistency Checks */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl text-white mb-4">🔍 Cross-Document Consistency</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(riskResults.consistency_checks).map(([check, details]) => (
                <div key={check} className="bg-gray-700 p-4 rounded-lg text-center">
                  <div className="text-2xl mb-2">{getStatusIcon(details.status)}</div>
                  <p className="text-white font-medium capitalize">{check}</p>
                  <p className="text-gray-400 text-sm">{details.issues} issues found</p>
                </div>
              ))}
            </div>
          </div>

          {/* Fraud Indicators */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl text-white mb-4">🛡️ Fraud Detection Analysis</h4>
            <div className="space-y-3">
              {Object.entries(riskResults.fraud_indicators).map(([indicator, status]) => (
                <div key={indicator} className="flex items-center justify-between">
                  <span className="text-gray-300 capitalize">{indicator.replace('_', ' ')}</span>
                  <span className={status ? 'text-green-400' : 'text-red-400'}>
                    {status ? '✅ Clear' : '❌ Flagged'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Compliance Check */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl text-white mb-4">📋 USCIS Compliance Check</h4>
            <div className="space-y-3">
              {Object.entries(riskResults.compliance_check).map(([check, status]) => (
                <div key={check} className="flex items-center justify-between">
                  <span className="text-gray-300 capitalize">{check.replace('_', ' ')}</span>
                  <span className="text-green-400">✅ {status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Risk Report */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl text-white mb-4">📊 Detailed Risk Report</h4>
            <div className="space-y-4">
              {(riskResults.detailed_risks || []).map((risk, index) => (
                <div key={index} className={`p-4 rounded-lg border-l-4 ${
                  risk.severity === 'High' ? 'border-red-500 bg-red-900/10' :
                  risk.severity === 'Medium' ? 'border-yellow-500 bg-yellow-900/10' :
                  'border-green-500 bg-green-900/10'
                }`}>
                  <div className="flex items-start justify-between mb-2">
                    <h5 className="text-white font-semibold">{risk.type}</h5>
                    <span className={`px-2 py-1 rounded text-xs ${
                      risk.severity === 'High' ? 'bg-red-600 text-white' :
                      risk.severity === 'Medium' ? 'bg-yellow-600 text-white' :
                      'bg-green-600 text-white'
                    }`}>
                      {risk.severity}
                    </span>
                  </div>
                  <p className="text-gray-400 mb-2">{risk.description}</p>
                  <p className="text-blue-400 text-sm">
                    <strong>Recommendation:</strong> {risk.recommendation}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={scanningRisks}
          className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${
            !scanningRisks
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Recommendations →
        </button>
      </div>
    </div>
  );
};